// utils/api/DocumentEditorApi/EmbeddedQuestionsApi.js
/**
 * API functions for embedded questions functionality
 */

import { getAuthHeaders } from '../ApiUtils';

const API_BASE_URL = '';

/**
 * Submit a student answer to an embedded question
 *
 * @param {Object} params - Submission parameters
 * @param {string} params.documentId - Document ID
 * @param {string} params.questionId - Question ID (e.g., "Q1")
 * @param {string} params.questionType - "mcq" or "short"
 * @param {Object} params.answerData - Answer data
 * @param {Array<string>} params.answerData.selected_options - For MCQ
 * @param {string} params.answerData.text_answer - For short answer
 * @param {any} params.rubric - Question rubric for validation
 * @param {Object} classContext - Class context with class_id and class_code
 * @returns {Promise<Object>} Submission result with validation
 */
export const submitAnswer = async ({ documentId, questionId, questionType, answerData, rubric }, classContext = null) => {
  try {
    const headers = getAuthHeaders();
    if (classContext?.class_id) {
      headers['Class-ID'] = classContext.class_id;
    }
    
    const response = await fetch(`${API_BASE_URL}/api/document-editor/submit-answer`, {
      method: 'POST',
      headers: headers,
      credentials: 'include',
      body: JSON.stringify({
        documentId,
        questionId,
        questionType,
        answerData,
        rubric
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to submit answer');
    }

    return data;
  } catch (error) {
    console.error('Error submitting answer:', error);
    throw error;
  }
};

/**
 * Get student's answers for a document
 *
 * @param {string} documentId - Document ID
 * @param {Object} classContext - Class context with class_id
 * @param {string} [questionId] - Optional question ID filter
 * @returns {Promise<Object>} Student answers and progress
 */
export const getStudentAnswers = async (documentId, classContext, questionId = null) => {
  try {
    let url = `${API_BASE_URL}/api/document-editor/student-answers/${documentId}`;
    if (questionId) {
      url += `?questionId=${encodeURIComponent(questionId)}`;
    }

    const headers = getAuthHeaders();

    // Add Class-ID header if classContext is provided
    if (classContext && classContext.class_id) {
      headers['Class-ID'] = classContext.class_id;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
      credentials: 'include'
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get student answers');
    }

    return data;
  } catch (error) {
    console.error('Error getting student answers:', error);
    throw error;
  }
};

/**
 * Grade a short answer question (instructor/TA only)
 *
 * @param {Object} params - Grading parameters
 * @param {string} params.answerId - Answer ID
 * @param {boolean} params.isCorrect - Whether answer is correct
 * @param {string} params.feedback - Grading feedback
 * @returns {Promise<Object>} Grading result
 */
export const gradeAnswer = async ({ answerId, isCorrect, feedback }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/document-editor/grade-answer`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        answerId,
        isCorrect,
        feedback
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to grade answer');
    }

    return data;
  } catch (error) {
    console.error('Error grading answer:', error);
    throw error;
  }
};

/**
 * Get latest answer for a specific question
 *
 * @param {Object} progress - Progress object from getStudentAnswers
 * @param {string} questionId - Question ID
 * @returns {Object|null} Latest answer data or null
 */
export const getLatestAnswer = (progress, questionId) => {
  return progress?.[questionId] || null;
};

/**
 * Check if student has answered a question
 *
 * @param {Object} progress - Progress object from getStudentAnswers
 * @param {string} questionId - Question ID
 * @returns {boolean} Whether question has been answered
 */
export const hasAnswered = (progress, questionId) => {
  return progress?.[questionId]?.answered === true;
};

/**
 * Check if answer is correct
 *
 * @param {Object} progress - Progress object from getStudentAnswers
 * @param {string} questionId - Question ID
 * @returns {boolean|null} True if correct, false if incorrect, null if not graded
 */
export const isCorrect = (progress, questionId) => {
  return progress?.[questionId]?.is_correct;
};
